import { useEffect, useMemo, useState } from "react";
import { normalizeString, xactimateBadgeFromSearchData } from "@rebuild/shared";
import { fetchXactimateMappingsForLineItem } from "../utils/xactimateMappingsAPI";

/**
 * Fetches the xactimate mappings for a line item template and returns a lookup from a normalized
 * permutation string to its cat/sel badge (e.g. "DRY | DERA"). Permutations generated client-side by
 * {@link getAllValidPermutations} are also normalized, so callers can key into this map directly.
 */
export const useLineItemPermutationCatSels = (lineItemId: string | undefined) => {
  const [badgeByPermutation, setBadgeByPermutation] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!lineItemId) {
      setBadgeByPermutation(new Map());
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let cancelled = false;

    (async () => {
      try {
        const mappings = await fetchXactimateMappingsForLineItem(lineItemId);
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const mapping of mappings) {
          const permutation = mapping.acceptedLineString?.trim();
          if (!permutation) continue;
          const badge = xactimateBadgeFromSearchData({ xactCat: mapping.cat, xactSel: mapping.variable });
          if (badge) {
            map.set(normalizeString(permutation), badge);
          }
        }
        setBadgeByPermutation(map);
      } catch {
        if (!cancelled) setBadgeByPermutation(new Map());
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lineItemId]);

  return useMemo(() => ({ badgeByPermutation, isLoading }), [badgeByPermutation, isLoading]);
};
